import React, { lazy, useState, useEffect } from 'react';
import { Modal, Table, Spin, Icon, Card, Row, Col, Badge } from 'antd';

const modalStyles = `
@keyframes mm-bar { 0%{background-position:0% center} 100%{background-position:200% center} }
@keyframes mm-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-3px)} }
@keyframes mm-beat  { 0%,100%{transform:scale(1)} 50%{transform:scale(1.2)} }
@keyframes mm-glow  { 0%,100%{box-shadow:0 0 0 0 rgba(24,144,255,0.4)} 50%{box-shadow:0 0 0 10px rgba(0,0,0,0)} }
@keyframes modal-pop { from{opacity:0;transform:scale(0.96) translateY(12px)} to{opacity:1;transform:scale(1) translateY(0)} }

.mm-modal .ant-modal-content { border-radius:14px!important; overflow:hidden!important; box-shadow:0 24px 64px rgba(0,0,0,.16),0 0 0 1px rgba(24,144,255,.1)!important; animation:modal-pop .3s cubic-bezier(.22,1,.36,1) both!important; }
.mm-modal .ant-modal-header  { padding:0!important; border-bottom:none!important; }
.mm-modal .ant-modal-body    { padding:20px 22px 24px!important; }
.mm-modal .ant-modal-close   { top:14px!important; right:16px!important; color:rgba(255,255,255,.8)!important; transition:color .2s,transform .2s!important; }
.mm-modal .ant-modal-close:hover { color:#fff!important; transform:rotate(90deg)!important; }
.mm-modal .ant-modal-close .ant-modal-close-x { width:36px!important; height:36px!important; line-height:36px!important; }

.mm-header {
  background: linear-gradient(135deg,#003a8c 0%,#0050b3 35%,#1890ff 70%,#096dd9 100%);
  padding:18px 22px 16px; display:flex; align-items:center; gap:14px;
  position:relative; overflow:hidden;
}
.mm-header::before { content:''; position:absolute; inset:0; background-image:radial-gradient(rgba(255,255,255,.09) 1px,transparent 1px); background-size:20px 20px; pointer-events:none; }
.mm-header::after  { content:''; position:absolute; bottom:0; left:0; right:0; height:3px; background:linear-gradient(90deg,#13c2c2,#1890ff,#52c41a,#fa8c16,#13c2c2); background-size:300% auto; animation:mm-bar 3s linear infinite; }
.mm-header-icon { width:44px; height:44px; border-radius:12px; background:rgba(255,255,255,.15); border:1.5px solid rgba(255,255,255,.25); display:flex; align-items:center; justify-content:center; flex-shrink:0; animation:mm-glow 2.6s ease-in-out infinite,mm-float 3s ease-in-out infinite; }
.mm-header-title { font-size:15px; font-weight:800; color:#fff; letter-spacing:.8px; }
.mm-header-sub   { font-size:11px; color:rgba(255,255,255,.6); letter-spacing:1px; margin-top:2px; }

/* Section cards */
.mm-section { border-radius:10px!important; border:1px solid #e6f0ff!important; box-shadow:0 2px 10px rgba(24,144,255,.06)!important; margin-bottom:16px!important; overflow:hidden!important; }
.mm-section-header { padding:11px 18px 10px; border-bottom:1px solid #f0f5ff; display:flex; align-items:center; gap:8px; background:linear-gradient(90deg,#f0f7ff,#fafcff); }
.mm-section-title  { font-size:11px; font-weight:700; color:#262626; letter-spacing:.5px; text-transform:uppercase; }

/* Empty state */
.mm-empty { display:flex; align-items:center; gap:8px; padding:14px 18px; font-size:12px; font-weight:600; color:#cf1322; }
.mm-empty .anticon { font-size:15px; }

/* Table row stripe */
.mm-row-stripe td { background:#f7f9ff!important; }

/* Qty badge */
.mm-qty { display:inline-block; padding:2px 8px; border-radius:8px; font-size:11px; font-weight:700; background:#e6f7ff; border:1px solid #91d5ff; color:#1890ff; }

/* Date cell */
.mm-date { display:inline-flex; align-items:center; gap:5px; font-size:12px; color:#595959; }
`;

const SectionHeader = ({ icon, color, bg, title, count, animClass }) => (
  <div className="mm-section-header">
    <div style={{ width:26, height:26, borderRadius:7, background:bg, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
      <Icon type={icon} className={animClass} style={{ fontSize:13, color }} />
    </div>
    <span className="mm-section-title">{title}</span>
    {count > 0 && <Badge count={count} style={{ backgroundColor: color, fontSize:10, fontWeight:600 }} />}
  </div>
);

const EmptyState = ({ message }) => (
  <div className="mm-empty">
    <Icon type="inbox" />
    {message}
  </div>
);

const MasterlistModal = props => {
  const { visible, closeModal, data, getJoDetails } = props;
  const [joData, setJoData] = useState({ pr: [], po: [], deliveredItems: [], releasedRm: [] });
  const [spinning, setSpinning] = useState(false);

  useEffect(() => {
    if (visible) {
      setSpinning(true);
      getJoDetails(data.id)
        .then(res => setJoData(res))
        .finally(() => setSpinning(false));
    }
  }, [visible]);

  const { pr, po, releasedRm, deliveredItems } = joData;

  const dateRender = val => val
    ? <span className="mm-date"><Icon type="calendar" style={{ color:'#1890ff', fontSize:11 }} />{val}</span>
    : <Icon type="ellipsis" style={{ color:'#d9d9d9' }} />;

  const qtyRender = val => <span className="mm-qty">{val}</span>;

  const itemCountRender = val => <span className="mm-qty">{val.items ? val.items.length : 0}</span>;

  const prColumns = [
    { key:'prNumber',  title:'PR NO.',      dataIndex:'prNumber', align:'center', width:'40%' },
    { key:'date',      title:'DATE',         dataIndex:'date',     align:'center', width:'30%', render: dateRender },
    { key:'items',     title:'ITEM COUNT',   align:'center',       width:'30%',    render: itemCountRender },
  ];

  const poColumns = [
    { key:'poNumber',  title:'PO NO.',       dataIndex:'poNumber', align:'center', width:'40%' },
    { key:'date',      title:'DATE',          dataIndex:'date',    align:'center', width:'30%', render: dateRender },
    { key:'items',     title:'ITEM COUNT',    align:'center',      width:'30%',    render: itemCountRender },
  ];

  const deliveredColumns = [
    { key:'code',          title:'CODE',                 dataIndex:'code',          align:'center', width:'18%' },
    { key:'materialSpecs', title:'MATERIAL SPEC.',       dataIndex:'materialSpecs', align:'center', width:'28%' },
    { key:'invoice',       title:'INVOICE NO.',          dataIndex:'invoice',       align:'center', width:'18%', render: val => val || <Icon type="ellipsis" style={{ color:'#d9d9d9' }} /> },
    { key:'dr',            title:'DELIVERY RECEIPT',     dataIndex:'dr',            align:'center', width:'18%', render: val => val || <Icon type="ellipsis" style={{ color:'#d9d9d9' }} /> },
    { key:'quantity',      title:'QUANTITY',             dataIndex:'quantity',      align:'center', width:'18%', render: qtyRender },
  ];

  const releasedColumns = [
    { key:'code',          title:'CODE',                 dataIndex:'code',          align:'center', width:'18%' },
    { key:'materialSpecs', title:'MATERIAL SPEC.',       dataIndex:'materialSpecs', align:'center', width:'28%' },
    { key:'quantity',      title:'QUANTITY',             dataIndex:'quantity',      align:'center', width:'18%', render: qtyRender },
    { key:'date',          title:'DATE',                 dataIndex:'date',          align:'center', width:'18%', render: dateRender },
    { key:'remarks',       title:'REMARKS',              dataIndex:'remarks',       align:'center', width:'18%', render: val => val || <Icon type="ellipsis" style={{ color:'#d9d9d9' }} /> },
  ];

  const renderTable = (cols, src) => (
    <Table
      columns={cols}
      rowKey="id"
      dataSource={src}
      bodyStyle={{ overflowX:'auto' }}
      pagination={false}
      size="small"
      bordered
      style={{ margin:0 }}
      rowClassName={(_, idx) => idx % 2 !== 0 ? 'mm-row-stripe' : ''}
    />
  );

  return (
    <>
      <style>{modalStyles}</style>
      <Modal
        className="mm-modal"
        style={{ top:10 }}
        visible={visible}
        footer={null}
        onCancel={closeModal}
        width={1200}
        title={
          <div className="mm-header">
            <div className="mm-header-icon">
              <Icon type="reconciliation" style={{ fontSize:20, color:'#fff' }} />
            </div>
            <div>
              <div className="mm-header-title">{data.jo_num || 'JOB ORDER DETAILS'}</div>
              <div className="mm-header-sub">PR · PO · Delivered & Released Materials</div>
            </div>
            <div style={{ marginLeft:'auto', display:'flex', gap:8, flexShrink:0 }}>
              {[
                { label:'PR',  count:pr.length,            color:'#1890ff' },
                { label:'PO',  count:po.length,            color:'#52c41a' },
                { label:'Del', count:deliveredItems.length, color:'#fa8c16' },
                { label:'Rel', count:releasedRm.length,    color:'#722ed1' },
              ].map(b => (
                <span key={b.label} style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:20, background:'rgba(255,255,255,.15)', border:'1px solid rgba(255,255,255,.25)', fontSize:11, fontWeight:700, color:'#fff' }}>
                  <span style={{ width:5, height:5, borderRadius:'50%', background:b.color, boxShadow:`0 0 5px ${b.color}` }} />
                  {b.label}: {b.count}
                </span>
              ))}
            </div>
          </div>
        }
      >
        <Spin spinning={spinning}>
          {/* PR */}
          <Card className="mm-section" bordered={false} bodyStyle={{ padding:0 }}>
            <SectionHeader icon="file-search" color="#1890ff" bg="#e6f7ff" title="Purchase Request List" count={pr.length} animClass="mm-float" />
            {pr.length > 0 ? renderTable(prColumns, pr) : <EmptyState message="No Purchase Request" />}
          </Card>

          {/* PO */}
          <Card className="mm-section" bordered={false} bodyStyle={{ padding:0 }}>
            <SectionHeader icon="shopping-cart" color="#52c41a" bg="#f6ffed" title="Purchase Order List" count={po.length} animClass="mm-beat" />
            {po.length > 0 ? renderTable(poColumns, po) : <EmptyState message="No Purchase Order" />}
          </Card>

          {/* Delivered RM */}
          <Card className="mm-section" bordered={false} bodyStyle={{ padding:0 }}>
            <SectionHeader icon="car" color="#fa8c16" bg="#fff7e6" title="Delivered Raw Materials" count={deliveredItems.length} animClass="mm-float" />
            {deliveredItems.length > 0 ? renderTable(deliveredColumns, deliveredItems) : <EmptyState message="No Delivered Raw Materials" />}
          </Card>

          {/* Released RM */}
          <Card className="mm-section" bordered={false} bodyStyle={{ padding:0 }}>
            <SectionHeader icon="export" color="#722ed1" bg="#f9f0ff" title="Released Raw Materials" count={releasedRm.length} animClass="mm-beat" />
            {releasedRm.length > 0 ? renderTable(releasedColumns, releasedRm) : <EmptyState message="No Released Raw Materials" />}
          </Card>
        </Spin>
      </Modal>
    </>
  );
};

export default MasterlistModal;